/**
 * Data hook for the All Targets scouting table
 *
 * Uses the `listAllScoutingTargets` query with server-side filtering,
 * search, and pagination. Filter variables are passed directly to the
 * query — Apollo re-fetches automatically when they change.
 * Positions are sorted with the primary position first using
 * `bioData.primaryPosition` from the GraphQL response (SE-4015).
 * Watchlists are deduplicated by ID to prevent duplicate names from
 * Apollo cache merge during pagination (SE-4022). Player rows are likewise
 * deduplicated by player id (SE-5404) because the same relay-style cache merge
 * can hold two edges for one player (distinct cursors) after a post-add
 * refetch, which would otherwise render a duplicate card. Targets whose
 * `player` is null are dropped before either step (SE-6250) — the backend now
 * permits a player-less target and a row is entirely player-derived.
 * @module features/scouting/hooks/useAllScoutingTargets
 */

import { useCallback, useMemo } from "react";
import { Platform } from "react-native";

import type { RangeFilter, ScoutingTargetRow } from "../types/scouting-target";

import {
  type OrderByDirection,
  type ScoutingListAllTargetsQuery,
  type SortField,
  useScoutingListAllTargetsQuery,
} from "@/generated/graphql";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { differenceInMonths } from "date-fns";

import { getNationalityFlagUrl } from "@/features/player-detail/utils/playerPageHeaderUtils";
import { resolveTargetAge } from "@/features/scouting/utils/resolveTargetAge";

/** Em dash fallback for missing text fields */
const EM_DASH = "\u2014";

/** Default page size for the scouting table */
const PAGE_SIZE = 50;

/** Type alias for a scouting target node from the query */
type ScoutingTargetNode =
  ScoutingListAllTargetsQuery["listAllScoutingTargets"]["edges"][number]["node"];

/** Type alias for a scouting target edge from the query */
type ScoutingTargetEdge =
  ScoutingListAllTargetsQuery["listAllScoutingTargets"]["edges"][number];

/**
 * A scouting-target node that is known to carry its player.
 * @remarks
 * SE-6250: `ScoutingTarget.player` became nullable on the backend (`Player!` →
 * `Player`). Everything downstream of {@link filterTargetsWithPlayer} works on
 * this narrowed shape, so the dozens of `target.player.<field>` reads below
 * stay direct and readable instead of growing optional chains — and the
 * narrowing is earned by a real runtime check, never by a cast.
 */
type ScoutingTargetNodeWithPlayer = Omit<ScoutingTargetNode, "player"> & {
  readonly player: NonNullable<ScoutingTargetNode["player"]>;
};

/** A scouting-target edge whose node is known to carry its player (SE-6250) */
type ScoutingTargetEdgeWithPlayer = Omit<ScoutingTargetEdge, "node"> & {
  readonly node: ScoutingTargetNodeWithPlayer;
};

/**
 * Drops scouting-target edges whose player is missing.
 * @remarks
 * SE-6250: `ScoutingTarget.player` is nullable on the backend. A target row is
 * entirely player-derived — name, club, positions, nationality, every metric —
 * so a target with no player has nothing renderable; a half-populated card of
 * em dashes would be worse than no card. Filtering once here (rather than
 * guarding each of the ~25 field reads in {@link toScoutingTargetRow}) keeps
 * the mapper total and its inputs non-null.
 * @param edges - Raw scouting-target edges from the query
 * @returns Only the edges whose node carries a player
 */
export const filterTargetsWithPlayer = (
  edges: readonly ScoutingTargetEdge[]
): readonly ScoutingTargetEdgeWithPlayer[] =>
  edges.filter(
    (edge): edge is ScoutingTargetEdgeWithPlayer => edge.node.player != null
  );

/**
 * Collapses scouting-target edges to one edge per player id, keeping the first
 * occurrence and preserving order.
 * @remarks
 * SE-5404: `listAllScoutingTargets` is configured with `relayStylePagination`
 * (see `providers/ApolloClient`). Adding the first player to a new list runs
 * the add mutation with `refetchQueries: ["ScoutingListAllTargets"]`; the
 * relay-style `merge` de-dupes incoming edges by CURSOR, not by node id, so the
 * refetched edge for the just-added player is appended alongside the existing
 * cached edge for the SAME player. Two edges → two rows → a duplicate card that
 * overlapped the search bar / list chips on small screen until a reload
 * rebuilt the cache from a single page. De-duping by player id at the read
 * boundary makes the relay-merge cursor duplication unobservable in the UI
 * without touching the shared pagination policy or mutating the cache. This
 * generalizes the SE-4022 watchlist-name de-dup (which only de-duped chips
 * within a row) to the player rows themselves.
 * @param edges - Player-carrying scouting-target edges (post relay merge)
 * @returns Edges with at most one entry per player id, original order preserved
 */
export const dedupeTargetsByPlayerId = (
  edges: readonly ScoutingTargetEdgeWithPlayer[]
): readonly ScoutingTargetEdgeWithPlayer[] =>
  edges.filter(
    (edge, index) =>
      edges.findIndex(
        candidate => candidate.node.player.id === edge.node.player.id
      ) === index
  );

/**
 * Sorts position abbreviations so the primary position appears first
 * @param abbreviations - Position abbreviations in backend order
 * @param primaryPosition - Primary position string from bioData (may be name or abbreviation)
 * @returns New array with primary position moved to front, others unchanged
 */
const sortPositionsPrimaryFirst = (
  abbreviations: readonly string[],
  primaryPosition: string | null | undefined
): readonly string[] => {
  const primaryIndex = primaryPosition
    ? abbreviations.findIndex(
        abbr => abbr.toLowerCase() === primaryPosition.toLowerCase()
      )
    : -1;

  return primaryIndex > 0
    ? [
        abbreviations[primaryIndex],
        ...abbreviations.filter((_, i) => i !== primaryIndex),
      ]
    : abbreviations;
};

/**
 * Parses the JSONB customStats from bioData into a numeric Record keyed by backend key.
 * Non-numeric values are silently skipped.
 * @param customStats - Raw JSONB custom stats from PlayerBioData
 * @returns Numeric lookup keyed by backend stat name
 */
const parseCustomStats = (
  customStats: Record<string, { value: string }> | null | undefined
): Readonly<Record<string, number>> =>
  customStats
    ? Object.entries(customStats).reduce<Record<string, number>>(
        (acc, [key, stat]) => {
          const num = parseFloat(stat?.value);
          return isNaN(num) ? acc : { ...acc, [key]: num };
        },
        {}
      )
    : {};

/**
 * Converts a scouting target node into a flat table row
 * @param target - GraphQL ScoutingTarget node with player + watchlists. Callers
 *   must pass a node that survived {@link filterTargetsWithPlayer} (SE-6250).
 * @param defaultWatchlistId - ID of the default watchlist, excluded from the Lists column (SE-5182)
 * @param selectedWatchlistId - Currently filtered watchlist ID — shown first in Lists column
 * @returns Flat table row shape for TanStack Table
 */
export const toScoutingTargetRow = (
  target: ScoutingTargetNodeWithPlayer,
  defaultWatchlistId?: string | null,
  selectedWatchlistId?: string | null
): ScoutingTargetRow => {
  const rawAbbreviations =
    target.player.positions
      ?.map(p => p?.abbreviation)
      .filter((a): a is string => Boolean(a)) ?? [];

  const sortedAbbreviations = sortPositionsPrimaryFirst(
    rawAbbreviations,
    target.player.bioData?.primaryPosition
  );

  // SE-5182: the default watchlist backs the "My Roster" org-roster filter, not a
  // curated list. Players land in it implicitly, so surfacing it as a per-player
  // "My Roster" chip mislabels them as belonging to a list they never joined.
  // Exclude it from the Lists column; real watchlists still show their names.
  const uniqueWatchlists = target.watchlists.reduce(
    (acc, wl) =>
      wl.id === defaultWatchlistId ? acc : { ...acc, [wl.id]: wl.name },
    {} as Record<string, string>
  );

  /** Sort entries so the selected/filtered watchlist appears first in Lists column */
  const unsortedEntries = Object.entries(uniqueWatchlists).map(
    ([id, name]) => ({ id, name })
  );
  const watchlistEntries = selectedWatchlistId
    ? [...unsortedEntries].sort((a, b) =>
        a.id === selectedWatchlistId ? -1 : b.id === selectedWatchlistId ? 1 : 0
      )
    : unsortedEntries;

  return {
    id: target.player.id,
    playerName: target.player.name || EM_DASH,
    clubName: target.player.team?.name ?? target.player.teamName ?? EM_DASH,
    position: sortedAbbreviations[0] ?? EM_DASH,
    allPositions: sortedAbbreviations.join(", ") || EM_DASH,
    positionId: target.player.positions?.[0]?.id ?? null,
    country: target.player.nationality?.name ?? EM_DASH,
    countryFlagUrl: getNationalityFlagUrl(
      target.player.nationality,
      Platform.OS
    ),
    nationalityId: target.player.nationality?.id ?? null,
    leagueName: target.player.league?.name ?? EM_DASH,
    leagueId: target.player.league?.id ?? null,
    gpr: target.player.bioData?.gpr ?? null,
    // v7 meta row (SE-6930): transactional DOB → provider DOB → provider age.
    age: resolveTargetAge(target.player),
    clubId: target.player.team?.id ?? null,
    watchlistNames: watchlistEntries.map(e => e.name).join(", ") || EM_DASH,
    watchlistEntries,
    teamStyleFit:
      target.player.bioData?.teamStyleFit != null
        ? Math.round(target.player.bioData.teamStyleFit)
        : null,
    playerStyleFit: target.player.bioData?.fitScore
      ? Math.round(target.player.bioData.fitScore * 100)
      : null,
    monthsOnContract: target.player.bioData?.contractExpires
      ? differenceInMonths(
          new Date(target.player.bioData.contractExpires),
          new Date()
        )
      : null,
    numberOfReports: target.scoutReportCount ?? null,
    avgScoutRating: target.avgScoutRating ?? null,
    scoutRatingScaleMax: target.scoutRatingScaleMax ?? null,
    carrying:
      target.player.bioData?.carryingTransGlobalCategoricalScore ?? null,
    dribbling:
      target.player.bioData?.dribblingTransGlobalCategoricalScore ?? null,
    passing: target.player.bioData?.passingTransGlobalCategoricalScore ?? null,
    shooting:
      target.player.bioData?.shotLocationTransGlobalCategoricalScore ?? null,
    gkPassing: target.player.bioData?.gkPassingTransCategoricalScore ?? null,
    gkPositioning:
      target.player.bioData?.gkPositioningTransCategoricalScore ?? null,
    gkShotStopping:
      target.player.bioData?.gkShotStoppingTransCategoricalScore ?? null,
    averageRatings: (target.averageRatings ?? []).reduce(
      (acc, rating) => ({ ...acc, [rating.name]: rating.average }),
      {} as Record<string, number>
    ),
    customMetrics: parseCustomStats(
      target.player.bioData?.customStats as
        | Record<string, { value: string }>
        | null
        | undefined
    ),
    // SE-6932: lastEditedAt/lastEditor, never updatedAt — see the field's own
    // description on ScoutingTarget for why (mechanical resequencing, not
    // human-attributed).
    lastEditedAt: target.lastEditedAt ?? null,
    editorName: target.lastEditor?.displayName ?? null,
  };
};

/** Server-side filter variables for the scouting query */
interface ScoutingQueryFilters {
  readonly search?: string;
  readonly filterByPositionIds?: readonly string[];
  readonly filterByTeamIds?: readonly string[];
  readonly filterByWatchlistIds?: readonly string[];
  readonly filterByScoutIds?: readonly string[];
  readonly filterByNationalityIds?: readonly string[];
  readonly filterByLeagueIds?: readonly string[];
  readonly filterByRoleArchetypes?: readonly string[];
  /** Range filters for the filter sheet sections */
  readonly age?: RangeFilter;
  /** GPV midpoint bounds (SE-6496) — players without a GPV are excluded while active */
  readonly geminiPlayerValuation?: RangeFilter;
  readonly contractMonths?: RangeFilter;
  readonly gpr?: RangeFilter;
  readonly fitScore?: RangeFilter;
  readonly teamStyleFit?: RangeFilter;
  readonly carrying?: RangeFilter;
  readonly passing?: RangeFilter;
  readonly dribbling?: RangeFilter;
  readonly shooting?: RangeFilter;
  readonly gkShotStopping?: RangeFilter;
  readonly gkPositioning?: RangeFilter;
  readonly gkPassing?: RangeFilter;
  readonly filterByOrganizationRoster?: boolean;
  /** Active team scope; forwarded to backend when set (SE-4931) */
  readonly teamId?: string | null;
}

/** Props for the useAllScoutingTargets hook */
interface UseAllScoutingTargetsProps {
  /** Whether the user is authenticated */
  readonly hasAuth: boolean;
  /** Server-side filter variables */
  readonly filters?: ScoutingQueryFilters;
  /**
   * Server-side sort field (SE-4146). Passed to the query's `sortBy` argument
   * for fields the backend can order (GPR, SCOUT_RATING, PIPELINE_STAGE).
   * Omit to fall back to the query's default ordering / client-side sort.
   */
  readonly sortBy?: SortField;
  /** Server-side sort direction (SE-4146). Paired with {@link sortBy}. */
  readonly sortOrder?: OrderByDirection;
  /** Skip the query entirely (e.g. when roster resolution failed) */
  readonly skip?: boolean;
  /** Default watchlist ID — watchlists matching this ID display as "My Roster" */
  readonly defaultWatchlistId?: string | null;
  /** Currently filtered watchlist ID — shown first in Lists column */
  readonly selectedWatchlistId?: string | null;
}

/** Return type for useAllScoutingTargets */
interface UseAllScoutingTargetsReturn {
  /** Flat table rows for TanStack Table consumption */
  readonly rows: readonly ScoutingTargetRow[];
  /** Total count of players matching the current filters */
  readonly totalCount: number;
  /** Whether data is still loading (initial or refetch) */
  readonly loading: boolean;
  /** Load more callback for pagination */
  readonly loadMore: () => void;
  /** Whether more pages are available */
  readonly hasMore: boolean;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /** Ref to attach to a scrollable container for automatic scroll-based pagination */
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** GraphQL query error, if any */
  readonly error: Error | undefined;
  /** Re-executes the query from the network */
  readonly refetch: () => Promise<unknown>;
  /**
   * The exact variables the table queries with (PV18, SE-8360) — the export
   * re-runs the same read with them so the file holds the table's result set.
   */
  readonly queryVariables: Readonly<Record<string, unknown>>;
}

/**
 * Spreads a range filter into min/max query variables if bounds are set
 * @param range - Optional range filter with min/max
 * @param prefix - Variable name prefix (e.g. "gpr" → minGpr/maxGpr)
 * @returns Object with minX/maxX keys, or empty object if no bounds set
 */
const spreadRange = (
  range: RangeFilter | undefined,
  prefix: string
): Record<string, number> => ({
  ...(range?.min != null
    ? { [`min${prefix[0].toUpperCase()}${prefix.slice(1)}`]: range.min }
    : {}),
  ...(range?.max != null
    ? { [`max${prefix[0].toUpperCase()}${prefix.slice(1)}`]: range.max }
    : {}),
});

/**
 * Spreads a non-empty array filter into a query variable, otherwise nothing
 * @param values - Optional array of ids
 * @param key - Variable name to assign the array under
 * @returns Object with the key set, or empty object if values are missing/empty
 */
const spreadIdArray = <T>(
  values: readonly T[] | undefined,
  key: string
): Record<string, readonly T[]> =>
  values && values.length > 0 ? { [key]: values } : {};

/**
 * Builds the query variables object from filter props, omitting empty arrays
 * @param filters - Filter selections from the container
 * @returns Variables object for the GraphQL query
 */
export const buildQueryVariables = (
  filters?: ScoutingQueryFilters
): Record<string, unknown> => ({
  first: PAGE_SIZE,
  ...(filters?.search ? { search: filters.search } : {}),
  ...spreadIdArray(filters?.filterByPositionIds, "filterByPositionIds"),
  ...spreadIdArray(filters?.filterByTeamIds, "filterByTeamIds"),
  ...spreadIdArray(filters?.filterByWatchlistIds, "filterByWatchlistIds"),
  ...spreadIdArray(filters?.filterByScoutIds, "filterByScoutIds"),
  ...spreadIdArray(filters?.filterByNationalityIds, "filterByNationalityIds"),
  ...spreadIdArray(filters?.filterByLeagueIds, "filterByLeagueIds"),
  ...spreadIdArray(filters?.filterByRoleArchetypes, "filterByRoleArchetypes"),
  ...spreadRange(filters?.age, "age"),
  ...spreadRange(filters?.geminiPlayerValuation, "geminiPlayerValuation"),
  ...spreadRange(filters?.contractMonths, "contractMonths"),
  ...spreadRange(filters?.gpr, "gpr"),
  ...spreadRange(filters?.fitScore, "fitScore"),
  ...spreadRange(filters?.teamStyleFit, "teamStyleFit"),
  ...spreadRange(filters?.carrying, "carrying"),
  ...spreadRange(filters?.passing, "passing"),
  ...spreadRange(filters?.dribbling, "dribbling"),
  ...spreadRange(filters?.shooting, "shooting"),
  ...spreadRange(filters?.gkShotStopping, "gkShotStopping"),
  ...spreadRange(filters?.gkPositioning, "gkPositioning"),
  ...spreadRange(filters?.gkPassing, "gkPassing"),
  ...(filters?.filterByOrganizationRoster
    ? { filterByOrganizationRoster: true }
    : {}),
  ...(filters?.teamId ? { teamId: filters.teamId } : {}),
});

/**
 * Fetches all scouting targets with server-side filtering and pagination
 * @param props - Auth state and filter variables
 * @param props.hasAuth - Whether the user is authenticated
 * @param props.filters - Server-side filter variables
 * @param props.sortBy - Server-side sort field (SE-4146)
 * @param props.sortOrder - Server-side sort direction (SE-4146)
 * @param props.skip - Skip the query entirely (e.g. when roster resolution failed)
 * @param props.defaultWatchlistId - Default watchlist ID to rename to "My Roster" in rows
 * @param props.selectedWatchlistId - Currently filtered watchlist ID — shown first in Lists column
 * @returns Table rows, count, loading state, pagination controls, error, and refetch
 */
const useAllScoutingTargets = ({
  hasAuth,
  filters,
  sortBy,
  sortOrder,
  skip: skipQuery,
  defaultWatchlistId,
  selectedWatchlistId,
}: UseAllScoutingTargetsProps): UseAllScoutingTargetsReturn => {
  // SE-4146: server-side sort variables ride alongside the filter variables so
  // they participate in the same normalized query entry and are forwarded to
  // every fetchMore (via extraVariables below).
  const queryVariables = useMemo(
    () => ({
      ...buildQueryVariables(filters),
      ...(sortBy ? { sortBy } : {}),
      ...(sortOrder ? { sortOrder } : {}),
    }),
    [filters, sortBy, sortOrder]
  );

  const { data, loading, error, fetchMore, refetch, networkStatus } =
    useScoutingListAllTargetsQuery({
      variables: queryVariables,
      skip: !hasAuth || Boolean(skipQuery),
      notifyOnNetworkStatusChange: true,
    });

  const targets = useMemo(
    () => data?.listAllScoutingTargets?.edges ?? [],
    [data?.listAllScoutingTargets?.edges]
  );

  const rows = useMemo(
    () =>
      // SE-6250: drop player-less targets first — `ScoutingTarget.player` is
      // nullable on the backend and a target row is entirely player-derived.
      // SE-5404: then de-dupe by player id before mapping — the relay-style
      // cache merge can hold two edges for the same player (distinct cursors)
      // after a post-add refetch, which would otherwise render a duplicate card.
      dedupeTargetsByPlayerId(filterTargetsWithPlayer(targets)).map(edge =>
        toScoutingTargetRow(edge.node, defaultWatchlistId, selectedWatchlistId)
      ),
    [targets, defaultWatchlistId, selectedWatchlistId]
  );

  const totalCount = data?.listAllScoutingTargets?.totalCount ?? 0;

  const { loadMore, hasMore, isLoadingMore, scrollContainerRef } =
    useInfiniteScroll({
      pageInfo: data?.listAllScoutingTargets?.pageInfo,
      fetchMore,
      networkStatus,
      extraVariables: queryVariables,
    });

  const handleRefetch = useCallback(
    () =>
      refetch().catch((err: unknown) => {
        console.error("Scouting targets refetch failed:", err);
      }),
    [refetch]
  );

  return {
    rows,
    totalCount,
    loading,
    loadMore,
    hasMore,
    isLoadingMore,
    scrollContainerRef,
    error,
    refetch: handleRefetch,
    queryVariables,
  };
};

export default useAllScoutingTargets;
